import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import Constants from "../utils/constants";
import { useAPIs } from "../apis/APIsConfig";
import { useForm } from "../utils/hooks";
import WithLoginLayout from "../hocs/layouts/LoginLayout";
import {
  recoverPass,
  tieneCorreo,
  getPreguntasSeguridad,
  validarRespuestas,
  registrarCorreoRecuperacion,
} from "../services/user";
import axios from "axios";
import InputMask from "react-input-mask";
import Reaptcha from "reaptcha";

const MAX_INTENTOS_PREGUNTAS = 3;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Pasos previos al flujo normal de recuperación (envío de código).
const STEP = {
  CEDULA: "cedula",
  PREGUNTAS: "preguntas",
  REGISTRAR: "registrar",
};

const RecoverPassword = () => {
  useAPIs();
  const navigate = useNavigate();

  var captchaRef;

  const [step, setStep] = useState(STEP.CEDULA);
  const [succeed, setSucceed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Estado de preguntas de seguridad.
  const [preguntas, setPreguntas] = useState<any[]>([]);
  const [intentos, setIntentos] = useState(0);
  const [bloqueado, setBloqueado] = useState(false);
  const [preguntasError, setPreguntasError] = useState("");

  // Estado del registro de correo.
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [registroError, setRegistroError] = useState("");
  const [cedulaError, setCedulaError] = useState("");

  const [formState, setError, changeHandler, reset] = useForm({
    cedula: "",
    captcha: false,
  });

  useEffect(() => {
    setSucceed(false);
    changeHandler("captcha", false)();
    captchaRef?.reset();
  }, []);

  function disableButton() {
    return (
      formState.data.cedula.replaceAll("_", "").length !== 11 ||
      !formState.data.captcha ||
      loading
    );
  }

  function normalizarCedula(cedula: string) {
    return cedula.replace(/-/g, "").replace(/_/g, "");
  }

  // Reanuda el flujo normal: envía el código de 6 dígitos y muestra la pantalla de confirmación.
  function reanudarRecuperacion() {
    const cedula = normalizarCedula(formState.data.cedula);
    const enviarRecuperacion = (ip: string) => {
      recoverPass(cedula, ip)
        .then((res) => {
          if (res?.data?.successed) {
            setSucceed(true);
          } else {
            const mensaje =
              res?.data?.message?.[0]?.message ||
              "No fue posible enviar el código de verificación. Inténtalo nuevamente.";
            setRegistroError(mensaje);
            setConfirmando(false);
          }
        })
        .catch(() => {
          setRegistroError(
            "Ocurrió un error al enviar el código de verificación. Inténtalo nuevamente."
          );
          setConfirmando(false);
        });
    };

    axios
      .get("https://api.ipify.org/?format=json", { timeout: 5000 })
      .then((resIP) => enviarRecuperacion(resIP?.data?.ip || ""))
      .catch(() => enviarRecuperacion(""));
  }

  function submitForm() {
    setCedulaError("");
    setLoading(true);
    tieneCorreo(normalizarCedula(formState.data.cedula))
      .then((res) => {
        if (!res?.data) {
          setCedulaError(
            "No fue posible verificar tu información. Inténtalo nuevamente."
          );
          return;
        }

        const tiene = res.data.value?.tieneCorreo;
        if (tiene === true) {
          reanudarRecuperacion();
          changeHandler("captcha", false)();
          captchaRef?.reset();
        } else if (tiene === false) {
          cargarPreguntas();
        } else {
          setCedulaError(
            "No fue posible verificar tu información. Inténtalo nuevamente."
          );
        }
      })
      .catch(() => {
        setCedulaError(
          "No fue posible verificar tu información. Inténtalo nuevamente."
        );
      })
      .finally(() => setLoading(false));
  }

  function cargarPreguntas() {
    setPreguntasError("");
    getPreguntasSeguridad(normalizarCedula(formState.data.cedula)).then((res) => {
      setPreguntas(res?.data?.value || []);
      setStep(STEP.PREGUNTAS);
    });
  }

  function handleChangeRespuesta(codPreguntas: number) {
    return (changeEvent) => {
      const pregs = [...preguntas];
      pregs.find((f) => f.codPreguntas === codPreguntas)["valorRespuesta"] =
        changeEvent.target.value;
      setPreguntas(pregs);
    };
  }

  function disablePreguntasButton() {
    return (
      loading ||
      preguntas.length === 0 ||
      preguntas.some((p) => !p.valorRespuesta)
    );
  }

  function submitPreguntas() {
    setPreguntasError("");
    setLoading(true);

    const respuestas = preguntas.map((preg) => ({
      codPregunta: preg.codPreguntas,
      valorRespuesta: preg.valorRespuesta,
    }));

    validarRespuestas(normalizarCedula(formState.data.cedula), respuestas)
      .then((res) => {
        if (res?.data?.successed) {
          setStep(STEP.REGISTRAR);
        } else {
          const nuevoIntento = intentos + 1;
          setIntentos(nuevoIntento);
          if (nuevoIntento >= MAX_INTENTOS_PREGUNTAS) {
            setBloqueado(true);
          } else {
            setPreguntasError(
              "Respuestas incorrectas. Intentos restantes: " +
                (MAX_INTENTOS_PREGUNTAS - nuevoIntento)
            );
          }
        }
      })
      .finally(() => setLoading(false));
  }

  function sanitizeEmail(value: string) {
    return value.replace(/[\sñÑ]/g, "");
  }

  function validarFormularioCorreo() {
    if (!EMAIL_REGEX.test(email)) {
      setEmailError("Ingresa un correo electrónico con formato válido.");
      return false;
    }
    if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
      setEmailError("Los correos electrónicos no coinciden.");
      return false;
    }
    setEmailError("");
    return true;
  }

  function continuarRegistro() {
    if (validarFormularioCorreo()) {
      setConfirmando(true);
    }
  }

  function confirmarRegistro() {
    setRegistroError("");
    setLoading(true);

    registrarCorreoRecuperacion(
      normalizarCedula(formState.data.cedula),
      email
    )
      .then((res) => {
        if (res && res.data && res.data.successed) {
          // Correo registrado en P2000: reanudar el flujo normal (envío de código).
          reanudarRecuperacion();
        } else {
          const mensaje =
            res?.data?.message?.[0]?.message ||
            "No fue posible registrar el correo. Inténtalo nuevamente.";
          setRegistroError(mensaje);
          setConfirmando(false);
        }
      })
      .catch(() => {
        setRegistroError(
          "Ocurrió un error al registrar el correo. Inténtalo nuevamente."
        );
        setConfirmando(false);
      })
      .finally(() => setLoading(false));
  }

  function onVerify(recaptchaResponse) {
    changeHandler("captcha", true)();
  }

  return (
    <>
      {/* PANTALLA DE ÉXITO (flujo normal reanudado: código de 6 dígitos) */}
      {succeed && (
        <div className="block text-center w-full max-w-[544px] mx-auto">
          <div className="w-20 h-20 rounded-full border-2 border-primary-90 p-2 mx-auto mb-10">
            <i className="icon icon-check  rounded-full bg-primary-20 text-primary-100 text-[20px] w-full h-full flex items-center justify-center"></i>
          </div>
          <h1 className="text-primary-100 font-bold mb-2">Solicitud Enviada</h1>
          <p className="text-gray-70 text-body-1 font-body font-normal mb-12">
            Hemos recibido tu solicitud. Revisa tu correo electrónico; te
            enviamos un <span className="font-semibold">código de 6 dígitos</span>.
            Ingresa ese código en la siguiente pantalla para restablecer tu contraseña.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(Constants.APPURLS.RESET_PASSWORD, {
                state: {
                  cedula: formState.data.cedula.replace(/-/g, "").replace(/_/g, ""),
                },
              })
            }
            className="btn btn-primary btn-large px-4 md:px-[112px] mb-4"
          >
            Ingresar código
          </button>
          <br />
          <Link
            to={Constants.APPURLS.LOGIN}
            className="btn btn-outline-secondary btn-large px-4 md:px-[112px]"
          >
            Ir a Inicio de Sesión
          </Link>
        </div>
      )}

      {/* PASO 1: CÉDULA */}
      {!succeed && step === STEP.CEDULA && (
        <>
          <Link
            to={Constants.APPURLS.LOGIN}
            className="btn btn-secondary btn-medium"
          >
            <i className="icon icon-arrow-leftv-short right"></i>Volver
          </Link>

          <div className="block pt-6 md:pt-14 pb-10">
            <h1 className="text-primary-100 font-bold mb-2">
              Recuperar Contraseña
            </h1>
            <p className="text-gray-70 text-body-1 font-body font-normal">
              Antes de continuar favor de{" "}
              <span className="font-semibold">
                ingresar su número de cédula
              </span>
            </p>
          </div>
          <form
            className="block space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="form-group">
              <label>Documento de Identidad</label>
              <InputMask
                type="text"
                className="form-control large"
                placeholder="Ingresa tu documento"
                value={formState.data.cedula}
                onChange={changeHandler("cedula", undefined, "-")}
                mask="999-9999999-9"
                maskPlaceholder={"_"}
              />
            </div>
            <Reaptcha
              ref={(e) => (captchaRef = e)}
              sitekey={process.env.REACT_APP_SITE_KEY}
              onVerify={onVerify}
            />
            {cedulaError && <p className="error">{cedulaError}</p>}
            <button
              type="button"
              onClick={submitForm}
              disabled={disableButton()}
              className="btn btn-primary btn-large w-full"
            >
              Continuar
            </button>
          </form>
        </>
      )}

      {/* PASO 2: PREGUNTAS DE SEGURIDAD (cuando no hay correo) */}
      {!succeed && step === STEP.PREGUNTAS && (
        <>
          <button
            type="button"
            onClick={() => setStep(STEP.CEDULA)}
            className="btn btn-secondary btn-medium"
          >
            <i className="icon icon-arrow-leftv-short right"></i>Volver
          </button>

          <div className="block pt-6 md:pt-14 pb-10">
            <h1 className="text-primary-100 font-bold mb-2">
              Validación de Identidad
            </h1>
            <p className="text-gray-70 text-body-1 font-body font-normal">
              No tienes un correo electrónico registrado. Responde las{" "}
              <span className="font-semibold">preguntas de seguridad</span> para
              continuar.
            </p>
          </div>

          {bloqueado ? (
            <div className="block">
              <p className="text-semantic-red text-body-1 font-body font-normal mb-8">
                Has superado el número de intentos permitidos. Inténtelo
                nuevamente en 15 minutos.
              </p>
              <Link
                to={Constants.APPURLS.LOGIN}
                className="btn btn-primary btn-large w-full"
              >
                Ir a Inicio de Sesión
              </Link>
            </div>
          ) : (
            <form
              className="block space-y-8"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {preguntas.map((pregunta, index) => (
                <div className="form-group" key={index}>
                  <label className="checkbox-radio">
                    <p className="font-body font-normal">
                      {pregunta.descripcion}
                    </p>
                  </label>

                  {pregunta.respuestas.map((respuesta, indexResp) => (
                    <div className="form-group mb-2" key={indexResp}>
                      <label className="checkbox-radio">
                        <input
                          type="radio"
                          value={respuesta.valor}
                          name={"pregunta" + pregunta.codPreguntas}
                          onChange={handleChangeRespuesta(
                            pregunta.codPreguntas
                          )}
                        />
                        <p>{respuesta.valor}</p>
                      </label>
                    </div>
                  ))}
                </div>
              ))}

              {preguntasError && <p className="error">{preguntasError}</p>}

              <button
                type="button"
                onClick={submitPreguntas}
                disabled={disablePreguntasButton()}
                className="btn btn-primary btn-large w-full"
              >
                Continuar
              </button>
            </form>
          )}
        </>
      )}

      {/* PASO 3: REGISTRAR CORREO */}
      {!succeed && step === STEP.REGISTRAR && (
        <>
          {!confirmando && (
            <button
              type="button"
              onClick={() => setStep(STEP.PREGUNTAS)}
              className="btn btn-secondary btn-medium"
            >
              <i className="icon icon-arrow-leftv-short right"></i>Volver
            </button>
          )}

          <div className="block pt-6 md:pt-14 pb-10">
            <h1 className="text-primary-100 font-bold mb-2">
              Registrar Correo Electrónico
            </h1>
            <p className="text-gray-70 text-body-1 font-body font-normal">
              {confirmando
                ? "Confirma que tu correo electrónico es correcto."
                : "Ingresa el correo electrónico que deseas registrar."}
            </p>
          </div>

          {confirmando ? (
            <div className="block space-y-8">
              <div className="form-group">
                <label>Correo Electrónico</label>
                <p className="text-gray-90 font-semibold break-all">{email}</p>
              </div>

              {registroError && <p className="error">{registroError}</p>}

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  disabled={loading}
                  className="btn btn-secondary btn-large w-full"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={confirmarRegistro}
                  disabled={loading}
                  className="btn btn-primary btn-large w-full"
                >
                  Confirmar
                </button>
              </div>
            </div>
          ) : (
            <form
              className="block space-y-8"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="form-group">
                <label>Correo Electrónico</label>
                <input
                  type="email"
                  className="form-control large"
                  placeholder="Ingresa tu correo electrónico"
                  value={email}
                  onChange={(e) => setEmail(sanitizeEmail(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "ñ" || e.key === "Ñ") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div className="form-group">
                <label>Confirmar Correo Electrónico</label>
                <input
                  type="email"
                  className="form-control large"
                  placeholder="Confirma tu correo electrónico"
                  value={confirmEmail}
                  onChange={(e) =>
                    setConfirmEmail(sanitizeEmail(e.target.value))
                  }
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "ñ" || e.key === "Ñ") {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => e.preventDefault()}
                />
              </div>

              {emailError && <p className="error">{emailError}</p>}

              <button
                type="button"
                onClick={continuarRegistro}
                disabled={!email || !confirmEmail || loading}
                className="btn btn-primary btn-large w-full"
              >
                Continuar
              </button>
            </form>
          )}
        </>
      )}
    </>
  );
};

export default WithLoginLayout(RecoverPassword);
